import { css } from "@emotion/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { trpcClient } from "../lib/trpc";
import { Button } from "@/components/button";
import { GridDisplay } from "@/components/GridDisplay";
import { validateGrid } from "@/components/Grid";
import type { Grid, GridValues } from "@/types/grid";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import {
  appContainer,
  appContainerFull,
  appHeader,
  appTitle,
  appSubtitle,
  loading as loadingStyle,
  error as errorStyle,
  emptyState,
} from "@/styles/shared";

const sliderContainer = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 24px;
  padding: 24px;
  min-height: 60vh;
`;

const navButton = css`
  flex-shrink: 0;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--accent);
  color: white;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

  svg {
    display: block;
    fill: currentColor;
  }

  &:hover:not(:disabled) {
    transform: scale(1.05);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const gridContainer = css`
  flex: 1;
  max-width: 900px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const progressIndicator = css`
  text-align: center;
  margin-bottom: 16px;
  font-size: var(--text-sm);
  color: var(--text-secondary);
`;

const inlineValidation = css`
  background: var(--container-bg);
  border-radius: var(--radius-xl);
  box-shadow: 0 8px 32px var(--shadow-dark);
  padding: 24px;
  width: 100%;
`;

const validationTitle = css`
  font-family: var(--font-heading);
  font-size: var(--text-lg);
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 20px 0;
  text-align: center;
`;

const validationContent = css`
  display: flex;
  gap: 24px;
  align-items: stretch;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const photoSection = css`
  flex: 1;
  min-width: 0;
`;

const photoImg = css`
  width: 100%;
  border-radius: var(--radius-sm);
  object-fit: contain;
  max-height: 400px;
`;

const gridSection = css`
  flex: 1;
  min-width: 0;
`;

const identifierField = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 16px;
  background: var(--surface-elevated);
  border-radius: var(--radius-lg);
  border: 1px solid var(--border-default);
  margin-bottom: 20px;
`;

const identifierLabel = css`
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const identifierInput = css`
  padding: 12px 20px;
  font-size: var(--text-xl);
  font-weight: 700;
  text-align: center;
  border: 2px solid var(--input-border);
  border-radius: var(--radius-md);
  background: var(--input-bg);
  color: var(--text-primary);
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  min-width: 200px;

  &:focus {
    border-color: var(--accent);
    box-shadow: 0 0 0 3px var(--accent-light, rgba(99, 102, 241, 0.2));
  }
`;

const identifierInputError = css`
  border-color: var(--color-error);
`;

const errorBox = css`
  background: var(--error-bg);
  border: 1px solid var(--color-error);
  border-radius: var(--radius-sm);
  padding: 12px 16px;
  color: var(--error-text);
  font-size: var(--text-sm);
  margin-bottom: 16px;
`;

const actionsRow = css`
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 20px;
`;

const IMAGE_BASE_URL = process.env.BUN_PUBLIC_CDN_URL;

type GridToValidate = Grid & { numbers: GridValues; identifier: string };

export function GridsToValidatePage() {
  const { t } = useTranslation();
  const [grids, setGrids] = useState<GridToValidate[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  // Editing state for current grid
  const [editedIdentifier, setEditedIdentifier] = useState("");
  const [identifierTouched, setIdentifierTouched] = useState(false);
  const [editedNumbers, setEditedNumbers] = useState<GridValues | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [duplicate, setDuplicate] = useState<{ existingGridId: string } | null>(null);

  const loadGrids = useCallback(async () => {
    setIsLoading(true);
    setErr(null);
    try {
      const result = await trpcClient.listGrids.query({
        limit: 100,
        offset: 0,
        status: "NumbersExtracted",
      });
      const gridsToValidate = result.items.filter(
        (g): g is GridToValidate => g.numbers !== null && g.numbers !== undefined
      ).map(g => ({
        ...g,
        identifier: g.identifier ?? "",
      }));
      setGrids(gridsToValidate);
      if (gridsToValidate.length > 0 && currentIndex >= gridsToValidate.length) {
        setCurrentIndex(0);
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load grids");
    } finally {
      setIsLoading(false);
    }
  }, [currentIndex]);

  useEffect(() => {
    loadGrids();
  }, []);

  const currentGrid = grids[currentIndex];

  // Track previous grid id to detect actual grid changes
  const prevGridIdRef = useRef<string | null>(null);

  // Reset editing state when grid changes
  useEffect(() => {
    if (currentGrid) {
      const isNewGrid = prevGridIdRef.current !== currentGrid.id;
      prevGridIdRef.current = currentGrid.id;

      if (isNewGrid) {
        setEditedIdentifier(currentGrid.identifier);
        setEditedNumbers(currentGrid.numbers);
        setIdentifierTouched(false);
        setSubmitError(null);
        setDuplicate(null);
      }
    }
  }, [currentGrid?.id]);

  const identifierError = editedIdentifier.trim() === "" ? t("gridValidation.identifierRequired") : null;

  const { errors: gridErrors, cellErrors, rowErrors } = useMemo(
    () => editedNumbers ? validateGrid(editedNumbers, t) : { errors: [], cellErrors: [], rowErrors: [] },
    [editedNumbers, t],
  );

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentIndex < grids.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handleConfirm = async () => {
    if (!currentGrid || identifierError) return;

    setSubmitting(true);
    setSubmitError(null);
    setDuplicate(null);
    try {
      const hasNumberChanges = JSON.stringify(editedNumbers) !== JSON.stringify(currentGrid.numbers);
      const hasIdentifierChanges = editedIdentifier !== currentGrid.identifier;

      await trpcClient.verifyGrid.mutate({
        gridId: currentGrid.id,
        ...(hasNumberChanges && editedNumbers ? { numbers: editedNumbers } : {}),
        ...(hasIdentifierChanges ? { identifier: editedIdentifier } : {}),
      });

      // Remove from list and move to next
      const newGrids = grids.filter(g => g.id !== currentGrid.id);
      setGrids(newGrids);
      if (currentIndex >= newGrids.length && newGrids.length > 0) {
        setCurrentIndex(newGrids.length - 1);
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : "Failed to confirm grid";
      // Check for duplicate identifier error: "DUPLICATE_IDENTIFIER:<gridId>"
      const match = message.match(/DUPLICATE_IDENTIFIER:(\S+)/);
      if (match) {
        setDuplicate({ existingGridId: match[1]! });
      } else {
        setSubmitError(message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCurrentGrid = async () => {
    if (!currentGrid) return;

    setSubmitting(true);
    try {
      await trpcClient.deleteGrid.mutate({ gridId: currentGrid.id });
      // Remove from list and move to next
      const newGrids = grids.filter(g => g.id !== currentGrid.id);
      setGrids(newGrids);
      if (currentIndex >= newGrids.length && newGrids.length > 0) {
        setCurrentIndex(newGrids.length - 1);
      }
      setDuplicate(null);
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : "Failed to delete grid");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkip = () => {
    handleNext();
  };

  if (isLoading) {
    return (
      <div css={appContainer}>
        <div css={loadingStyle}>{t("gridsToValidate.loading")}</div>
      </div>
    );
  }

  if (err) {
    return (
      <div css={appContainer}>
        <div css={errorStyle}>{err}</div>
      </div>
    );
  }

  return (
    <div css={[appContainer, appContainerFull]}>
      <header css={appHeader}>
        <div>
          <h1 css={appTitle}>{t("gridsToValidate.title")}</h1>
          <p css={appSubtitle}>
            {t("gridsToValidate.subtitle", { count: grids.length })}
          </p>
        </div>
        <Button variant="secondary" onClick={loadGrids}>
          {t("common.refresh")}
        </Button>
      </header>

      {grids.length === 0 ? (
        <div css={emptyState}>
          <p>{t("gridsToValidate.empty")}</p>
        </div>
      ) : (
        <div css={sliderContainer}>
          <button
            css={navButton}
            onClick={handlePrev}
            disabled={currentIndex === 0 || submitting}
            aria-label={t("common.previous")}
          >
            <CaretLeft size={28} weight="bold" />
          </button>

          <div css={gridContainer}>
            <div css={progressIndicator}>
              {t("gridsToValidate.progress", { current: currentIndex + 1, total: grids.length })}
            </div>

            {currentGrid && (
              <div css={inlineValidation}>
                <h3 css={validationTitle}>{t("gridValidation.title")}</h3>

                {/* Identifier field */}
                <div css={identifierField}>
                  <label css={identifierLabel} htmlFor="grid-identifier">
                    {t("grids.identifier")}
                  </label>
                  <input
                    id="grid-identifier"
                    type="text"
                    css={[identifierInput, identifierTouched && identifierError && identifierInputError]}
                    value={editedIdentifier}
                    onChange={(e) => setEditedIdentifier(e.target.value)}
                    onBlur={() => setIdentifierTouched(true)}
                    placeholder={t("grids.identifierPlaceholder")}
                    disabled={submitting}
                  />
                  {identifierTouched && identifierError && (
                    <span css={{ color: "var(--color-error)", fontSize: "var(--text-sm)" }}>
                      {identifierError}
                    </span>
                  )}
                </div>

                {submitError && (
                  <div css={errorBox}>{submitError}</div>
                )}

                {gridErrors.length > 0 && (
                  <div css={errorBox}>
                    {gridErrors.map((error, i) => (
                      <div key={i}>{error}</div>
                    ))}
                  </div>
                )}

                {duplicate && (
                  <div css={errorBox}>
                    {t("gridValidation.duplicateIdentifier", { identifier: editedIdentifier })}
                  </div>
                )}

                <div css={validationContent}>
                  <div css={photoSection}>
                    <img
                      src={`${IMAGE_BASE_URL}/${currentGrid.fileUrl}`}
                      alt={t("gridValidation.gridPhoto")}
                      css={photoImg}
                    />
                  </div>
                  <div css={gridSection}>
                    {editedNumbers && (
                      <GridDisplay
                        numbers={editedNumbers}
                        onChange={setEditedNumbers}
                        cellErrors={cellErrors}
                        rowErrors={rowErrors}
                      />
                    )}
                  </div>
                </div>

                <div css={actionsRow}>
                  <Button
                    variant="secondary"
                    onClick={handleSkip}
                    disabled={submitting || currentIndex >= grids.length - 1}
                  >
                    {t("gridsToValidate.skip")}
                  </Button>
                  {duplicate ? (
                    <Button
                      variant="danger"
                      onClick={handleDeleteCurrentGrid}
                      disabled={submitting}
                    >
                      {submitting ? t("common.deleting") : t("gridValidation.deleteCurrentGrid")}
                    </Button>
                  ) : (
                    <Button
                      variant="primary"
                      onClick={handleConfirm}
                      disabled={identifierError !== null || gridErrors.length > 0 || submitting}
                    >
                      {submitting ? t("gridValidation.confirming") : t("common.confirm")}
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>

          <button
            css={navButton}
            onClick={handleNext}
            disabled={currentIndex >= grids.length - 1 || submitting}
            aria-label={t("common.next")}
          >
            <CaretRight size={28} weight="bold" />
          </button>
        </div>
      )}
    </div>
  );
}
